import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import lang from '../../lib/language';
import { BaseStatementSearchParamType, StatementPeriodType } from '../../lib/models/fundamental/types';

import { useIsMobile } from '../../hooks';

type StatementItem = {
  calendarYear: number;
  period: string;
};

type LangKey = keyof typeof lang;

/**
 * The keys of T that have a label in the language file under `${P}${Capitalize<key>}`.
 * Reading a row for any other field is a compile error.
 */
type LabeledField<T, P extends string> = {
  [K in keyof T & string]: `${P}${Capitalize<K>}` extends LangKey ? K : never;
}[keyof T & string];

type StatementRows<T, P extends string> = Record<LabeledField<T, P>, Array<number | string>>;

type StatementTable<T, P extends string> = {
  headers: string[];
  row: StatementRows<T, P>;
};

const LABEL_COLUMN = 0;
const FIRST_FIGURE_COLUMN = LABEL_COLUMN + 1;

const ANNUAL_PERIOD = 'FY';

const twoDigitYear = (calendarYear: number) => String(calendarYear).slice(-2);

const formatHeader = (item: StatementItem) => (
  item?.period === ANNUAL_PERIOD
    ? String(item.calendarYear)
    : `${item?.period}'${twoDigitYear(item?.calendarYear)}`
);

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const rowLabel = (labelPrefix: string, field: string) => {
  const label = lang[`${labelPrefix}${capitalize(field)}` as LangKey];

  return typeof label === 'function' ? label() : undefined;
};

/**
 * Rows are built on first access, so the component decides which fields it shows
 * simply by reading `row.<apiField>`. Each row is the field's values, newest first,
 * followed by its label (or preceded by it on desktop, matching the headers).
 */
const buildRows = <T extends StatementItem, P extends string>(
  items: T[],
  labelPrefix: P,
): StatementRows<T, P> => {
  const cache: Partial<Record<string, Array<number | string>>> = {};

  return new Proxy(cache as StatementRows<T, P>, {
    get: (_, field) => {
      if (typeof field !== 'string') return undefined;

      const label = rowLabel(labelPrefix, field);

      if (label === undefined) return undefined;

      cache[field] ??= [
        ...items.map((item) => item?.[field as keyof T] as number | string),
        label,
      ].reverse();

      return cache[field];
    },
  });
};

export const buildStatementTable = <T extends StatementItem, P extends string>(
  items: T[],
  labelPrefix: P,
  isMobile: boolean,
): StatementTable<T, P> => {
  const headers = items.map(formatHeader);

  if (!isMobile) headers.push(lang.typeHeader());

  return { headers: headers.reverse(), row: buildRows(items, labelPrefix) };
};

type Params<T extends StatementItem, P extends string> = {
  items: T[];
  isReady: boolean;
  /** Prefix of this statement's lang keys: `${labelPrefix}${Capitalize<apiField>}`. */
  labelPrefix: P;
  getStatements: (params: BaseStatementSearchParamType) => void;
};

const useFinancialStatement = <T extends StatementItem, P extends string>({
  items,
  isReady,
  labelPrefix,
  getStatements,
}: Params<T, P>) => {
  const { isMobile } = useIsMobile();

  const { name } = useParams<'name'>();
  const symbol = name ?? '';

  const [period, setPeriod] = useState<StatementPeriodType>(StatementPeriodType.ANNUAL);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [table, setTable] = useState<StatementTable<T, P>>({
    headers: [],
    row: {} as StatementRows<T, P>,
  });

  useEffect(() => {
    getStatements({ symbol, periodType: period });
  }, [ getStatements, symbol, period ]);

  useEffect(() => {
    if (!isReady) return;

    setTable(buildStatementTable(items, labelPrefix, isMobile));
  }, [ items, isReady, labelPrefix, isMobile ]);

  const selectPeriod = useCallback((next: StatementPeriodType) => {
    setPeriod(next);
    setSelectedPeriodIndex(0);
  }, []);

  const { headers, row } = table;

  const timeOptions = useMemo(
    () => headers.filter((header) => header !== lang.typeHeader() || !isMobile),
    [ headers, isMobile ],
  );

  return {
    headers,
    isMobile,
    loading: headers.length === 0,
    period,
    row,
    selectedColumn: FIRST_FIGURE_COLUMN + selectedPeriodIndex,
    selectedPeriodIndex,
    selectPeriod,
    setSelectedPeriodIndex,
    timeOptions,
  };
};

export default useFinancialStatement;
